import React from 'react';
import { useHotkeys, Options, HotkeyCallback } from 'react-hotkeys-hook';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const defaultShortcuts = [
  { key: 'Esc', description: 'Cerrar pop-ups y vistas modales.' },
];

export const ShortcutsStateContext = React.createContext<{
  shortcuts: Shortcut[];
}>({ shortcuts: defaultShortcuts });

export const ShortcutsActionsContext = React.createContext<{
  setShortcuts: React.Dispatch<React.SetStateAction<Shortcut[]>>;
}>({ setShortcuts: () => {} });

const noop = () => {};

export const useShortcut = (
  key: string,
  description: string,
  callback?: HotkeyCallback,
  keyDescription?: string,
  options?: Options
) => {
  useHotkeys(key, callback || noop, options ?? {}, [callback]);
  const { setShortcuts } = React.useContext(ShortcutsActionsContext);
  React.useEffect(() => {
    setShortcuts((shortcuts) => [
      ...shortcuts,
      {
        key,
        description,
        callback,
        keyDescription,
      },
    ]);
    return () =>
      setShortcuts((shortcuts) => shortcuts.filter((item) => key !== item.key));
  }, [callback, description, key, keyDescription, setShortcuts]);
};

type Shortcut = {
  key: string;
  description: string;
  callback?: HotkeyCallback;
  keyDescription?: string;
};

export const ShortcutsProvider: React.FC<{ children?: React.ReactNode }> = (
  props
) => {
  const [shortcuts, setShortcuts] =
    React.useState<Shortcut[]>(defaultShortcuts);

  const actionsContext = React.useMemo(() => ({ setShortcuts }), []);
  const stateContext = React.useMemo(() => ({ shortcuts }), [shortcuts]);

  return (
    <ShortcutsActionsContext.Provider value={actionsContext}>
      <ShortcutsStateContext.Provider value={stateContext}>
        {props.children}
      </ShortcutsStateContext.Provider>
    </ShortcutsActionsContext.Provider>
  );
};

export const KeyboardHelpModal: React.FC<{}> = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const toggle = React.useCallback(() => setIsOpen((isOpen) => !isOpen), []);
  const openModal = React.useCallback(() => {
    setIsOpen(true);
  }, []);
  const { shortcuts } = React.useContext(ShortcutsStateContext);

  useShortcut('shift+?, h', 'Mostrar esta ventana de ayuda.', openModal, '?,h');

  return (
    <Dialog open={isOpen} onOpenChange={toggle}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Atajos de teclado</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-[1fr_5fr] gap-x-4 gap-y-2">
          {shortcuts.map(({ key, description, keyDescription }) => (
            <React.Fragment key={key}>
              <div>
                {(keyDescription || key)
                  .replace('+', ' plus ')
                  .replace(',', ' comma ')
                  .split(' ')
                  .filter((token) => token !== '')
                  .map((token) =>
                    token === 'comma' ? (
                      ', '
                    ) : token === 'plus' ? (
                      '+'
                    ) : (
                      <kbd
                        key={token}
                        className="inline-flex pointer-events-none h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 text-muted-foreground"
                      >
                        {token}
                      </kbd>
                    )
                  )}
              </div>
              <div>{description}</div>
            </React.Fragment>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
